/**
 * Centralized formatting utilities for consistent display across the app
 */

/**
 * Detect if a symbol represents a TradFi asset
 * TradFi assets include stocks, commodities, indices, etc.
 */
export const isTradFiAsset = (symbol: string): boolean => {
  const tradFiPatterns = [
    // Stocks
    'AMZN', 'AAPL', 'TSLA', 'GOOGL', 'MSFT', 'NVDA', 'META', 'NFLX',
    'SPY', 'QQQ', 'DIA', 'IWM',
    // Commodities
    'GOLD', 'SILVER', 'OIL', 'GAS',
    // HIP-3 examples
    'XYZ', 'BABA',
    // Generic patterns
    'STOCK', 'INDEX', 'COMMODITY'
  ];

  return tradFiPatterns.some(pattern =>
    symbol.toUpperCase().includes(pattern)
  );
};

/**
 * Format price with appropriate decimal places based on asset type and value
 * TradFi assets always show 2 decimals
 * Crypto assets use dynamic precision based on price
 * Includes comma separators for readability
 */
export const formatPrice = (price: number, symbol?: string): string => {
  let decimals: number;

  // TradFi assets always use 2 decimals (like traditional markets)
  if (symbol && isTradFiAsset(symbol)) {
    decimals = 2;
  }
  // Crypto assets use dynamic precision based on price magnitude
  else if (price >= 1000) decimals = 1;   // BTC: 50000.0
  else if (price >= 100) decimals = 2;    // ETH: 2500.00
  else if (price >= 10) decimals = 2;     // SOL: 95.50
  else if (price >= 1) decimals = 3;      // ADA: 0.350
  else if (price >= 0.01) decimals = 4;   // DOGE: 0.0850
  else decimals = 6;                       // SHIB: 0.000008

  return price.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format size/quantity with appropriate decimal places
 */
export const formatSize = (size: number): string => {
  return size.toLocaleString(undefined, {
    maximumFractionDigits: 4,
    minimumFractionDigits: 0
  });
};

/**
 * Format total value (size * price) with appropriate formatting
 */
export const formatTotal = (total: number): string => {
  if (total >= 1000000) {
    return `${(total / 1000000).toFixed(2)}M`;
  }
  if (total >= 1000) {
    return total.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  return total.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/**
 * Format volume with appropriate units (K, M, B)
 */
export const formatVolume = (volume: number): string => {
  if (volume >= 1e9) return `$${(volume / 1e9).toFixed(2)}B`;
  if (volume >= 1e6) return `$${(volume / 1e6).toFixed(2)}M`;
  if (volume >= 1e3) return `$${(volume / 1e3).toFixed(2)}K`;
  return `$${volume.toFixed(2)}`;
};

/**
 * Format percentage change with + sign for positive values
 */
export const formatPercentage = (percentage: number, decimals: number = 2): string => {
  const sign = percentage >= 0 ? '+' : '';
  return `${sign}${percentage.toFixed(decimals)}%`;
};

/**
 * Format USD amount with $ prefix and comma separators
 */
export const formatUSD = (amount: number, decimals: number = 2): string => {
  const sign = amount >= 0 ? '' : '-';
  const absAmount = Math.abs(amount);
  return `${sign}$${absAmount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};
